import { useSorting } from '@components/Sorting/Provider';
import {
  buyPathMap,
  parseBuyPath,
} from '../../providers/SearchRequestProvider/parsers';
import {
  PerformedSearchRequestContext,
  useNumberOfPassengers,
  usePerformedSearchRequest,
} from '../../providers/SearchRequestProvider/index';
import { TICKETS_LIMIT } from '@components/Results/Components/TicketsLeft/TicketsLeft';
import { HighlightedType } from '@components/Results/Components/ItineraryHeader/constants/highlightedTypes';
import { useDisplayedItinerariesQuery } from 'clientState/providers/DisplayedItinerariesProvider';
import { useUnbundle } from '../../providers/SearchResponseProvider/SearchItinerariesResponseProvider';
import { useSelectedUnbundleDepartureId } from '../../providers/SelectedUnbundleDepartureProvider/index';

export const TRIP_TYPE_MAP: Record<
  PerformedSearchRequestContext['tripType'],
  string
> = {
  ONE_WAY: 'OW',
  ROUND_TRIP: 'RT',
  MULTIPLE_DESTINATIONS: 'MD',
};

const CABIN_CLASS_MAP: Record<string, string> = {
  TOURIST: 'EC',
  PREMIUM_ECONOMY: 'PE',
  BUSINESS: 'BU',
  FIRST: 'FC',
  ECONOMIC_DISCOUNTED: 'ED',
};

export const SORTING_MAP: Record<string, string> = {
  recommended: 'BS',
  cheapest: 'CH',
  fastest: 'FA',
  earliest: 'EA',
};

const BUY_PATH_MAP: Record<string, number[]> = {
  HO: [
    buyPathMap.FLIGHTS_HOME_SEARCH_FORM,
    buyPathMap.FLIGHTS_HOME_RECENT_SEARCHES,
    buyPathMap.FLIGHTS_HOME_FLIGHTS_TAB,
  ],
  RE: [
    buyPathMap.FLIGHTS_RESULTS_SEARCH_FORM,
    buyPathMap.PASSENGER_PAGE_SEARCH,
  ],
  DE: [buyPathMap.WEEKEND_DEALS, buyPathMap.HOTTEST_DEALS],
  FD: [buyPathMap.CALENDAR_SEARCH_FLEXIBLE_DATES],
  WM: [buyPathMap.FLIGHTS_HOME_SEARCH_FORM_MICROSITE],
  SE: [buyPathMap.SEM_RESULTS],
  ER: [
    buyPathMap.FLIGHTS_EXPIRED_SEARCH_POPUP,
    buyPathMap.FLIGHTS_EXPIRED_SESSION_POPUP,
    buyPathMap.CONFIRMATION_REJECTED,
    buyPathMap.BOOKING_ERROR,
    buyPathMap.DYNPACK_EXPIRED_SEARCH_POPUP,
  ],
  IN: [
    buyPathMap.INSPIRATIONAL_SEARCH,
    buyPathMap.INSPIRATIONAL_DEALS_PAGE,
    buyPathMap.EDIT_INSPIRATIONAL_SEARCH,
  ],
  CR: [
    buyPathMap.KO_EMAIL_SAME_SEARCH,
    buyPathMap.TRANSACTIONAL_EMAILS,
    buyPathMap.BIM_NOTIFICATIONS_REBOOK_F,
  ],
  DP: [
    buyPathMap.FLIGHTS_HOME_DYNPACK_TAB,
    buyPathMap.DYNPACK_MOBILE_RESULTS_CHANGE_SEARCH,
    buyPathMap.DYNPACK_MOBILE_ACCOMMODATION_CHANGE_SEARCH,
    buyPathMap.DYNPACK_MOBILE_BOOKING_CHANGE_SEARCH,
    buyPathMap.DYNPACK_HOME_DYNPACK_TAB,
    buyPathMap.DYNPACK_RESULTS_SEARCH_FORM,
    buyPathMap.DYNPACK_RESULTS_ACCOMMODATION_SEARCH_FORM,
    buyPathMap.DYNPACK_RESULTS_FLIGHTS_SEARCH_FORM,
    buyPathMap.DYNPACK_ROOM_SELECTION_SEARCH_FORM,
    buyPathMap.DYNPACK_DIRECT_URL,
    buyPathMap.DYNPACK_EXPIRED_ROOMSELECTION_POPUP,
    buyPathMap.UPSELL_DYNPACK_EMERGING_LAYER_REDIRECTION,
    buyPathMap.DYNPACK_MOBILE_HOME,
    buyPathMap.DYNPACK_HOME_DYNPACK_TAB_MICROSITE,
    buyPathMap.DYNPACK_HOME_FLIGHTS_TAB,
    buyPathMap.DYNPACK_MOBILE_FLIGHTS_TAB,
  ],
  IP: [
    buyPathMap.INTERMEDIATE_PAGE_FLIGHTS_SEARCH,
    buyPathMap.INTERMEDIATE_PAGE_DYNPACK_SEARCH,
  ],
  LP: [
    buyPathMap.SSLP_AIRLINES_INDEX,
    buyPathMap.SSLP_AIRLINE_CITY_PAIRS,
    buyPathMap.SSLP_AIRLINES,
    buyPathMap.SSLP_CITY_PAIRS,
    buyPathMap.SSLP_COUNTRIES,
    buyPathMap.SSLP_DYNPACK_DESTINATIONS,
    buyPathMap.SSLP_FLIGHT_DESTINATIONS,
    buyPathMap.SSLP_GENERIC,
    buyPathMap.SSLP_INTERNATIONAL,
    buyPathMap.SSLP_HOTELS,
    buyPathMap.SSLP_CITY,
    buyPathMap.SSLP_CARS_INTERNATIONAL,
    buyPathMap.SSLP_CARS_COUNTRIES,
    buyPathMap.SSLP_CARS_DESTINATIONS,
    buyPathMap.SSLP_CARS_HOME,
    buyPathMap.SSLP_SEARCHER,
    buyPathMap.SSLP_CITY_COUNTRY,
    buyPathMap.SSLP_CITY_STATE,
    buyPathMap.SSLP_TRAINS_DESTINATION,
    buyPathMap.SSLP_TRAINS_CITY_PAIRS,
    buyPathMap.SSLP_TRAIN_CARRIER,
    buyPathMap.SSLP_SEARCHEXTENSION,
    buyPathMap.SSLP_HOTELS_PROPERTIES,
    buyPathMap.SSLP_TRAINS,
  ],
  OT: [
    buyPathMap.RESERVED_4,
    buyPathMap.BIM_NOTIFICATIONS_REBOOK_DP,
    buyPathMap.POST_BOOKING_ACCOMMODATION,
    buyPathMap.UNKNOWN,
  ],
  UR: [buyPathMap.FLIGHTS_DIRECT_URL],
  MA: [
    buyPathMap.HSA_FLIGHT_CONFIRMATION,
    buyPathMap.HSA_RESULTS_SEARCH_FORM,
    buyPathMap.HSA_PB_SEARCHEXTENSION,
    buyPathMap.HOME_HSA_TAB,
    buyPathMap.HSA_PB_POPUNDER,
    buyPathMap.HSA_HOME,
    buyPathMap.HSA_ROOM_SELECTION_SEARCH_FORM,
    buyPathMap.HSA_DIRECT_URL,
    buyPathMap.HSA_HOME_DEALS,
    buyPathMap.HSA_PB_YOURJOURNEY,
    buyPathMap.HSA_CRM,
    buyPathMap.HSA_LANDING_WMP,
    buyPathMap.HSA_MAP,
    buyPathMap.HSA_NATIVE_SEARCH_FORM,
    buyPathMap.HSA_ROOM_SHARE,
    buyPathMap.HSA_PB_EMERGINGLAYER,
    buyPathMap.HSA_PB_GIFT,
    buyPathMap.HSA_PB_MOBILE_DEALS,
  ],
};

const BAGGAGE_MAP: Record<string, string> = {
  CHECKIN_INCLUDED: 'BB',
  CABIN_INCLUDED: 'SB',
  CABIN_INCLUDED_US: 'SB',
  BASED_ON_AIRLINE: 'SB',
};

const getCabinClass = (cabinClass) => CABIN_CLASS_MAP[cabinClass] || 'NA';
const getCardsWithTicketsLeft = (itineraries) =>
  itineraries
    ?.slice(0, 10)
    .filter(
      ({ ticketsLeft, transportTypes }) =>
        !transportTypes?.every((type) => type === 'TRAIN') &&
        ticketsLeft > TICKETS_LIMIT.MIN &&
        ticketsLeft < TICKETS_LIMIT.MAX
    ).length || 0;

const getCardsWithCrown = (itineraries) =>
  itineraries
    ?.slice(0, 10)
    .filter(({ highlightedType }) => highlightedType != HighlightedType.Default)
    .length || 0;

const getBuyPathLabel = (buyPath) => {
  const buyPathId = parseBuyPath(buyPath);

  const buyPathLabel =
    Object.entries(BUY_PATH_MAP).find(([_, values]) => {
      return values.includes(buyPathId);
    })?.[0] || buyPathId;

  return buyPathLabel || 'NA';
};

const getNumberStops = (segment) => {
  return segment?.stops?.length || 0;
};

const getInfoFromItinerary = (itinerary) => {
  const departure = itinerary?.legs ? itinerary?.legs[0]?.segments[0] : null;
  const arrival = itinerary?.legs
    ? itinerary?.legs[itinerary.legs.length - 1]?.segments[0]
    : null;

  const numberStopsDeparture = getNumberStops(departure);
  const numberStopsArrival = getNumberStops(arrival);

  const positionCardPriceRank = itinerary?.priceSortingPosition || 'NA';

  const prices = {
    noPrime: itinerary?.fees.find(
      (fee) => fee.type.id === 'MEMBER_PRICE_POLICY_UNDISCOUNTED'
    )?.price,
    prime: itinerary?.fees.find(
      (fee) => fee.type.id === 'MEMBER_PRICE_POLICY_DISCOUNTED'
    )?.price,
  };

  const hasCrown = +(itinerary?.highlightedType !== 'default');

  const hasTicketRemaining = +(itinerary?.ticketsLeft !== 0);

  return {
    departure,
    arrival,
    numberStopsDeparture,
    numberStopsArrival,
    positionCardPriceRank,
    prices,
    hasCrown,
    hasTicketRemaining,
  };
};

export const determinePageType = (search) => {
  if (!search?.itinerary?.segments) {
    return 'comb_f_t';
  }

  const segments = search.itinerary.segments;
  const allGeoNodeTypes = new Set();

  segments.forEach((segment) => {
    if (segment?.departure?.geoNodeType) {
      allGeoNodeTypes.add(segment.departure.geoNodeType);
    }
    if (segment?.destination?.geoNodeType) {
      allGeoNodeTypes.add(segment.destination.geoNodeType);
    }
  });

  const geoNodeTypesArray = Array.from(allGeoNodeTypes);
  const hasAirports = geoNodeTypesArray.includes('AIRPORT');
  const hasTrainStations = geoNodeTypesArray.includes('TRAIN_STATION');

  if (hasAirports && hasTrainStations) {
    return 'comb_f_t';
  } else if (hasTrainStations && !hasAirports) {
    return 'train';
  } else if (hasAirports && !hasTrainStations) {
    return 'flight';
  } else {
    return 'comb_f_t';
  }
};

const getInfoFromSearch = (search) => {
  const { tripType, itinerary, buyPath } = search || {};

  const tripSelected = TRIP_TYPE_MAP[tripType];

  const segments = itinerary?.segments || [];
  const departure = segments[0];
  const arrival = segments[segments.length - 1];

  const departureDate = departure?.date.replace(/-/g, '');
  const returnDate =
    tripSelected === 'OW' ? 'NA' : arrival?.date.replace(/-/g, '');

  const buyPathLabel = getBuyPathLabel(buyPath);

  const cabinClassLabel = getCabinClass(itinerary?.cabinClass);

  const departureGeoNodeTypeLabel =
    departure?.departure?.geoNodeType === 'AIRPORT' ? 'AO' : null;
  const arrivalGeoNodeTypeLabel =
    (tripSelected === 'OW' ? arrival?.destination : arrival?.departure)
      ?.geoNodeType === 'AIRPORT'
      ? 'AI'
      : null;

  const directFlightsOnly = search?.withoutConnections ? 'D' : null;

  const filterApplied = [
    departureGeoNodeTypeLabel,
    arrivalGeoNodeTypeLabel,
    directFlightsOnly,
  ].filter((v) => v != null);

  const filterAppliedLabel =
    filterApplied.length === 0
      ? 'NA'
      : filterApplied.length > 1
      ? 'MT'
      : filterApplied[0];

  const iataDeparture = departure?.departure?.iata;
  const iataArrival =
    tripSelected === 'RT'
      ? arrival?.departure?.iata
      : arrival?.destination?.iata;

  const geoNodeDeparture = departure?.departure?.geoNodeId;
  const geoNodeReturn =
    tripSelected === 'RT'
      ? arrival?.departure?.geoNodeId
      : arrival?.destination?.geoNodeId;

  return {
    tripSelected,
    departureDate,
    returnDate,
    buyPathLabel,
    cabinClassLabel,
    iataDeparture,
    iataArrival,
    geoNodeDeparture,
    geoNodeReturn,
    filterAppliedLabel,
  };
};

export const useResultsDataMeshTracking = () => {
  const { sorting } = useSorting();
  const numPassengers = useNumberOfPassengers();
  const search = usePerformedSearchRequest();
  const { data } = useDisplayedItinerariesQuery();
  const { isUnbundle } = useUnbundle();
  const unbundleDepartureId = useSelectedUnbundleDepartureId();

  const getUIType = () => {
    return isUnbundle ? (unbundleDepartureId ? 'RET' : 'DEP') : 'ALL';
  };

  const createDMTrackingLabelOnClickCard = ({ itinerary, isPrime }) => {
    if (!search) return '';

    const {
      tripSelected,
      departureDate,
      returnDate,
      buyPathLabel,
      cabinClassLabel,
      iataDeparture,
      iataArrival,
      geoNodeDeparture,
      geoNodeReturn,
    } = getInfoFromSearch(search) || {};

    const {
      departure,
      arrival,
      numberStopsDeparture,
      numberStopsArrival,
      positionCardPriceRank,
      prices,
      hasCrown,
      hasTicketRemaining,
    } = getInfoFromItinerary(itinerary) || {};

    const page = determinePageType(search);

    const sortingLabel = SORTING_MAP[sorting];

    const positionCard =
      data?.itineraries?.findIndex((item) => item?.id === itinerary?.id) + 1;

    const getCurrentPrice = () => (isPrime ? prices.prime : prices.noPrime);
    const currentPrice = getCurrentPrice();
    const price = currentPrice
      ? `${currentPrice.amount.toFixed(2)}${currentPrice.currency || 'EUR'}`
      : 'NA';

    const primePriceLabel = isPrime ? 'P' : 'O';

    const uiType = getUIType();

    const departureBaggageType =
      BAGGAGE_MAP[departure?.baggageCondition] || 'NO';
    const returnBaggageType =
      tripSelected !== 'OW'
        ? BAGGAGE_MAP[arrival?.baggageCondition] || 'NO'
        : 'NA';

    const isReturnPage = isUnbundle && unbundleDepartureId;

    const selectedOutboundAirline = isReturnPage
      ? departure?.carrierId || 'NA'
      : 'NA';
    const currentFlightAirline = departure?.carrierId || 'NA';

    const getPriceDifference = () => {
      if (!isReturnPage) return 'NA';

      const selectedDeparture = data?.itineraries?.find(
        (item) => item.id === unbundleDepartureId
      );

      if (!selectedDeparture) return 'NA';

      const selectedOutboundPrice = isPrime
        ? selectedDeparture?.fees?.find(
            (fee) => fee.type.id === 'MEMBER_PRICE_POLICY_DISCOUNTED'
          )?.price
        : selectedDeparture?.fees?.find(
            (fee) => fee.type.id === 'MEMBER_PRICE_POLICY_UNDISCOUNTED'
          )?.price;

      const currentPrice = getCurrentPrice();

      if (!selectedOutboundPrice || !currentPrice) return 'NA';

      const priceDifference = Math.abs(
        currentPrice.amount - selectedOutboundPrice.amount
      );
      const currency = currentPrice.currency || 'EUR';

      const result = `${priceDifference.toFixed(2)}${currency}`;
      return result;
    };

    const getCurrentInboundPrice = () => {
      if (!isReturnPage) return 'NA';

      const inboundPrice = getCurrentPrice();
      if (!inboundPrice) return 'NA';

      const currency = inboundPrice.currency || 'EUR';
      const result = `${inboundPrice.amount.toFixed(2)}${currency}`;
      return result;
    };

    const priceDifference = getPriceDifference();
    const currentInboundPrice = getCurrentInboundPrice();

    const trackingLabel = [
      `card_selected:${page}`,
      `ui:${uiType}`,
      `type:${tripSelected}`,
      `pp:${numPassengers}`,
      `dd:${departureDate}`,
      `dr:${returnDate}`,
      `sort:${sortingLabel}`,
      `buypath:${buyPathLabel}`,
      `ct:${hasTicketRemaining}`,
      `cc:${hasCrown}`,
      `pos:${positionCard}`,
      `posp:${positionCardPriceRank}`,
      `prime:${primePriceLabel}`,
      `cl:${cabinClassLabel}`,
      `oi:${iataDeparture}`,
      `di:${iataArrival}`,
      `lgd:${departureBaggageType}`,
      `lgr:${returnBaggageType}`,
      `si:${numberStopsDeparture}`,
      `so:${numberStopsArrival}`,
      `og:${geoNodeDeparture}`,
      `dg:${geoNodeReturn}`,
      `itid:${itinerary?.id}`,
      `prc:${price}`,
      `al:${selectedOutboundAirline}`,
      `po:${currentFlightAirline}`,
      `prco:${priceDifference}`,
      `prci:${currentInboundPrice}`,
    ].join('_');

    return trackingLabel;
  };

  const getSearchLaunchedLabel = ({
    departureDate,
    returnDate,
  }: {
    departureDate: string;
    returnDate?: string;
  }) => {
    // This should never happen because flex dates would never render with no
    // search parameters, it's only here for typing
    if (!search) return '';

    const { tripType, itinerary } = search;
    const { segments } = itinerary;

    const page = determinePageType(search);
    const tripSelected = TRIP_TYPE_MAP[tripType];
    const departureDateFormatted = departureDate.replace(/-/g, '');
    const returnDateFormatted = returnDate
      ? returnDate.replace(/-/g, '')
      : 'NA';
    // Buypath will always change to flex dates after search
    const buyPathLabel = getBuyPathLabel(81);
    const cabinClassLabel = getCabinClass(itinerary?.cabinClass);
    const outboundSegment = segments[0];
    const iataDeparture = outboundSegment.departure.iata;
    const iataArrival = outboundSegment.destination.iata;
    const geoNodeDeparture = outboundSegment.departure.geoNodeId;
    const geoNodeReturn = outboundSegment.destination.geoNodeId;

    return [
      `search_launched:${page}`,
      `type:${tripSelected}`,
      `pp:${numPassengers}`,
      `dd:${departureDateFormatted}`,
      `dr:${returnDateFormatted}`,
      // Sorting will always be the default one after performing a search
      `sort:BS`,
      `buypath:${buyPathLabel}`,
      `cl:${cabinClassLabel}`,
      `oi:${iataDeparture}`,
      `di:${iataArrival}`,
      `og:${geoNodeDeparture}`,
      `dg:${geoNodeReturn}`,
    ].join('_');
  };

  const createDMTrackingLabelResultsOnLoad = () => {
    const {
      tripSelected,
      departureDate,
      returnDate,
      buyPathLabel,
      cabinClassLabel,
      iataDeparture,
      iataArrival,
      geoNodeDeparture,
      geoNodeReturn,
      filterAppliedLabel,
    } = getInfoFromSearch(search) || {};

    const page = determinePageType(search);

    const uiType = isUnbundle ? 'U' : 'B';

    const sortingLabel = SORTING_MAP[sorting];

    const numberCardsWithTicketsLeft = getCardsWithTicketsLeft(
      data?.itineraries
    );

    const numberCardsWithCrown = getCardsWithCrown(data?.itineraries);

    return [
      `results_onload:${page}`,
      `ui:${uiType}`,
      `type:${tripSelected}`,
      `pp:${numPassengers}`,
      `dd:${departureDate}`,
      `dr:${returnDate}`,
      `sort:${sortingLabel}`,
      `buypath:${buyPathLabel}`,
      `ct:${numberCardsWithTicketsLeft}`,
      `cc:${numberCardsWithCrown}`,
      `cl:${cabinClassLabel}`,
      `oi:${iataDeparture}`,
      `di:${iataArrival}`,
      `og:${geoNodeDeparture}`,
      `dg:${geoNodeReturn}`,
      `fp:${filterAppliedLabel}`,
    ].join('_');
  };

  return {
    createDMTrackingLabelResultsOnLoad,
    createDMTrackingLabelOnClickCard,
    getSearchLaunchedLabel,
  };
};
